import React, { useEffect } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { Carousel } from 'react-responsive-carousel'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'

import {
  useGetMateriText,
  usePostMateriProgress,
} from 'api/microlearning/useAPIMateri'
import { GATrackEvent } from 'utils/googleAnalytics'
import QueryNotCompleteError from '../components/QueryNotCompleteError'
import useMateriRouterCheck from '../hooks/useMateriRouterCheck'
import TextMateriContent from '../partials/Materi/TextMateriContent'
import TextMateriPageNavigation from '../partials/Materi/TextMateriPageNavigation'

import type { MouseEvent } from 'react'

const MateriText = ({ isDesktop = false }) => {
  const { query, isReady: isRouterReady, back } = useRouter()
  const isRouterInvalid = useMateriRouterCheck(isRouterReady, query)
  const {
    isSuccess,
    isLoading,
    data: materiContent,
  } = useGetMateriText(
    query.modul as string,
    query.materi as string,
    query.id as string
  )
  const { recordProgress = 'true' } = query
  const shouldRecordProgress = recordProgress === 'true'

  const [currentPage, setCurrentPage] = React.useState(0)
  const isProgressSent = React.useRef<boolean>(false)
  const { mutateAsync: saveProgress } = usePostMateriProgress(
    query.modul as string,
    query.materi as string,
    {
      onSuccess: () => {
        isProgressSent.current = true
      },
    }
  )

  useEffect(() => {
    if (
      isSuccess &&
      !!materiContent?.data?.contents &&
      materiContent.data.contents.length === 1 &&
      !isProgressSent.current
    ) {
      shouldRecordProgress &&
        saveProgress({ id: materiContent?.data?.id, type: 'text' })
    }
  }, [isSuccess, materiContent?.data, saveProgress, shouldRecordProgress])

  useEffect(() => {
    if (isSuccess) {
      GATrackEvent({
        eventName: 'learning_activity',
        eventParams: {
          event_trigger: 'module_open_text',
          action_type: 'open_material_text',
          meta: JSON.stringify({
            learning_id: materiContent?.data?.id,
            material_id: query.materi,
            module_id: query.modul,
          }),
        },
        sendDefaultParams: true,
      })
    }
  }, [isSuccess, materiContent, query])

  const handleOnPageChange = async (index) => {
    setCurrentPage(index)
    if (
      !isProgressSent.current &&
      index + 1 >= materiContent?.data?.contents?.length
    ) {
      shouldRecordProgress &&
        (await saveProgress({ id: materiContent?.data?.id, type: 'text' }))
    }
  }

  const gotoPreviousPage = (event: MouseEvent) => {
    event.preventDefault()
    setCurrentPage((oldPage: number) => {
      if (oldPage === 0) {
        return oldPage
      }
      return oldPage - 1
    })
    window.scrollTo(0, 0)
  }

  const gotoNextPage = (event: MouseEvent) => {
    event.preventDefault()
    setCurrentPage((oldPage: number) => {
      if (oldPage + 1 >= materiContent?.data?.contents?.length) {
        return oldPage
      }
      return oldPage + 1
    })
    window.scrollTo(0, 0)
  }

  if (isRouterInvalid) {
    return <QueryNotCompleteError />
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="p-4 bg-white border border-subdued rounded-lg">
          <Shimmer
            height={12}
            noOfLines={9}
            className="mb-4 last:mb-0"
            style={{ width: '100%' }}
          />
        </div>
      </div>
    )
  }

  if (isDesktop) {
    return (
      <>
        <Head>
          <title>
            Pelatihan Mandiri | Materi {materiContent?.data?.name ?? ' Text'}
          </title>
        </Head>
        <div className="flex flex-row">
          {query.from && (
            <Icon as="i" className="mr-2" onClick={back}>
              arrow_back
            </Icon>
          )}
          <div>
            <Text className="pb-1">MATERI</Text>
            <Text className="pb-1" variant="heading-lg">
              {materiContent?.data?.name ?? ' Text'}
            </Text>
          </div>
        </div>
        <div
          className="relative py-[18px]"
          style={{
            minHeight: 'calc(100vh - 11rem)',
          }}
        >
          <Carousel
            interval={1000 * 60 * 60} //hack until fixed on https://github.com/leandrowd/react-responsive-carousel/pull/624
            showThumbs={false}
            autoPlay={false}
            selectedItem={currentPage}
            showArrows={false}
            showStatus={false}
            showIndicators={false}
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
            onChange={handleOnPageChange}
          >
            {materiContent?.data?.contents.map((content, index) => {
              return (
                <div
                  className={`${isDesktop ? '' : 'px-4'} text-left`}
                  key={index}
                  data-testid={`page-${index + 1}`}
                >
                  <TextMateriContent
                    title={materiContent.data.name}
                    content={content}
                    isDesktop={isDesktop}
                  />
                </div>
              )
            })}
          </Carousel>
          <TextMateriPageNavigation
            currentIndex={currentPage}
            totalPage={materiContent?.data?.contents.length}
            onPrevious={gotoPreviousPage}
            onNext={gotoNextPage}
          />
        </div>
      </>
    )
  }

  return (
    isSuccess && (
      <>
        <Head>
          <title>
            Pelatihan Mandiri | Materi {materiContent?.data?.name ?? ' Text'}
          </title>
        </Head>

        <div
          className="relative py-[18px] bg-gray-5"
          style={{
            minHeight: 'calc(100vh - 3.5rem)',
          }}
        >
          <Carousel
            interval={1000 * 60 * 60} //hack until fixed on https://github.com/leandrowd/react-responsive-carousel/pull/624
            showThumbs={false}
            autoPlay={false}
            selectedItem={currentPage}
            showArrows={false}
            showStatus={false}
            showIndicators={false}
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
            onChange={handleOnPageChange}
          >
            {materiContent?.data?.contents.map((content, index) => {
              return (
                <div
                  className={`${isDesktop ? '' : 'px-4'} text-left`}
                  key={index}
                  data-testid={`page-${index + 1}`}
                >
                  <TextMateriContent
                    title={materiContent?.data.name}
                    content={content}
                    isDesktop={isDesktop}
                  />
                </div>
              )
            })}
          </Carousel>
          <TextMateriPageNavigation
            currentIndex={currentPage}
            totalPage={materiContent?.data?.contents.length}
            onPrevious={gotoPreviousPage}
            onNext={gotoNextPage}
          />
        </div>
      </>
    )
  )
}

export default MateriText
